<script setup lang="ts">
// Imports
import gsap from 'gsap';
import SplitText from 'gsap/SplitText';
import CustomEase from 'gsap/CustomEase';
import { StructuredText } from 'vue-datocms';
import { isEmptyDocument } from 'datocms-structured-text-utils';
import Gradient from '~/components/partials/Gradient.vue';
import AssetContainer from '~/components/partials/AssetContainer.vue';
import Grid from '~/components/partials/Grid.vue';
import { useHeaderIntersection } from '~/composables/useHeaderIntersection';
import ScrollIndicator from '~/components/partials/ScrollIndicator.vue';
// Types
import type { HeaderMediaFragment } from '#gql';

// Props
const props = defineProps<{
  data: HeaderMediaFragment;
}>();

// Refs
const isLoaded = ref(false);
const containerElementRef = ref<null | HTMLElement>(null);
const frontElementRef = ref<null | HTMLElement>(null);
const titleElementRef = ref<null | HTMLElement>(null);

// Variables
const hasText = !isEmptyDocument(props.data.text);
let customEaseOut: any;
let lastWidth = 0;
let split: SplitText | null = null;
let childSplit: SplitText | null = null;
let resizeTimeout: ReturnType<typeof setTimeout>;

// Composable
useHeaderIntersection(containerElementRef, {
  elementStart: 'top',
  elementEnd: '50%',
});

// Register plugins
gsap.registerPlugin(SplitText);
gsap.registerPlugin(CustomEase);

// Methods
const initializeOnScroll = () => {
  const timeline = gsap.timeline({
    scrollTrigger: {
      trigger: containerElementRef.value,
      start: 'top top',
      end: 'bottom top',
      scrub: 0.4,
    },
  });

  timeline.to(frontElementRef.value, {
    clipPath: 'inset(0 0 80% 0)',
  });
};

const introAnimation = () => {
  // Kill previous splits
  split?.revert();
  childSplit?.revert();

  split = new SplitText(titleElementRef.value, {
    type: 'lines',
  });

  childSplit = new SplitText(split.lines, {
    type: 'lines',
  });

  // Set outer lines to overflow hidden and add padding/margin to compensate for the lines' height
  gsap.set(split.lines, {
    overflow: 'hidden',
    paddingBottom: '0.15em',
    marginBottom: '-0.15em',
  });

  // Move inner lines to bottom
  childSplit.lines.forEach((line, index) => {
    gsap.set(line, {
      y: `${(index + 1) * 150}%`,
    });
  });

  // Animate inner lines to top
  gsap.to(childSplit.lines, {
    y: '0%',
    duration: 1,
    ease: customEaseOut,
  });
};

const onResize = () => {
  clearTimeout(resizeTimeout);
  resizeTimeout = setTimeout(() => {
    const currentWidth = window.innerWidth;
    if (Math.abs(currentWidth - lastWidth) > 100) {
      introAnimation();
      lastWidth = currentWidth;
    }
  }, 150);
};

// Lifecycle
onMounted(() => {
  // Define custom ease
  customEaseOut = CustomEase.create('custom', '0.75, 0, 0.25, 1');

  // Set last width
  if (typeof window !== 'undefined') {
    lastWidth = window.innerWidth;
  }

  // Show the hidden text
  isLoaded.value = true;

  // Initialize animations
  introAnimation();
  initializeOnScroll();

  // Re-run intro animation on resize
  if (typeof window !== 'undefined') {
    window.addEventListener('resize', onResize);
  }
});

onUnmounted(() => {
  // Remove event listener on unmount
  window.removeEventListener('resize', onResize);
});
</script>

<template>
  <section class="header-media" :class="{ '-is-loaded': isLoaded }">
    <div class="container" ref="containerElementRef">
      <!-- Back -->
      <div class="back" aria-hidden="true">
        <Grid class="grid" with-bleed>
          <span class="title text-3xl">
            {{ data.title }}
          </span>
          <ScrollIndicator variant="dark" :scroll-text="data.scrollText" />
        </Grid>
      </div>
      <!-- Front -->
      <div class="front" ref="frontElementRef">
        <Gradient />
        <AssetContainer class="asset" :asset="data.asset" />
        <Grid class="grid" with-bleed>
          <h1 class="title text-3xl" ref="titleElementRef">
            {{ data.title }}
          </h1>
          <ScrollIndicator variant="light" :scroll-text="data.scrollText" />
        </Grid>
      </div>
    </div>
    <Grid class="content" with-bleed v-if="hasText">
      <StructuredText class="text" :data="data.text" />
    </Grid>
  </section>
</template>

<style lang="scss">
.header-media > .container {
  position: relative;
  height: 100svh;
}

.header-media > .container > .front,
.header-media > .container > .back {
  position: absolute;
  inset: 0;
}

.header-media > .container > .front > .grid,
.header-media > .container > .back > .grid {
  position: absolute;
  width: 100%;

  @media (--vs) {
    bottom: 7.5rem;
  }
  @media (--vl) {
    bottom: 7.5rem;
  }
}

.header-media > .container > .front > .grid > .title {
  color: var(--color-white);
  opacity: 0;
}

.header-media.-is-loaded > .container > .front > .grid > .title {
  opacity: 1;
}

.header-media > .container > .front > .grid > .title,
.header-media > .container > .back > .grid > .title {
  display: inline-block;

  @media (--vl) {
    grid-column: 1/7;
  }
}

.header-media > .content {
  @media (--vs) {
    padding-bottom: 3rem;
  }

  @media (--vl) {
    padding-bottom: 5rem;
  }
}

.header-media > .content > .text {
  @media (--vl) {
    grid-column: 2/8;
  }
}
</style>
